import type { ConnectionState } from 'livekit-client'
import type { ParticipantState, VideoQuality, ScreenShareQuality } from '~/lib/livekit/types'
import type { Group, GroupDetails, GroupUser } from '~/composables/useGroups'
import type { User } from '~/stores/user'

/** Window label the debug window is always created with (see `toggle_debug_window` in `lib.rs`). */
export const DEBUG_WINDOW_LABEL = 'debug'
/** Window label the main call/chat window is always created with (see `run()` in `lib.rs`). */
export const MAIN_WINDOW_LABEL = 'main'

export const DEBUG_EVENTS = {
  /** debug window -> main: "I just opened, send me everything you've got." */
  requestSnapshot: 'debug:request-snapshot',
  /** main -> debug: full current state, sent once per request plus on every meaningful change. */
  snapshot: 'debug:snapshot',
  /** main -> debug: a single new log line, streamed live as it happens. */
  log: 'debug:log',
  /** main -> debug: the log ring buffer accumulated before the debug window existed/asked. */
  logBatch: 'debug:log-batch',
  /**
   * main -> debug: local mic 0..1 level, streamed continuously off a Web Audio `AnalyserNode`
   * (the same source as `<MicLevelMeter>`) — instant, unlike `liveKit.participants[].audioLevel`
   * which only updates at LiveKit's own polled `ActiveSpeakersChanged` cadence.
   */
  micLevel: 'debug:mic-level',
  /** debug window -> main: force the mobile layout on/off, same override `usePlatform()` persists
   * for `?mobile=1`/`?mobile=0` in localStorage — lets you flip it without reloading with a query
   * param. */
  setMobileLayout: 'debug:set-mobile-layout',
} as const

export type DebugLogCategory = 'livekit' | 'system'
export type DebugLogLevel = 'info' | 'warn' | 'error'

export interface DebugLogEntry {
  id: number
  ts: number
  category: DebugLogCategory
  level: DebugLogLevel
  message: string
}

export interface DebugLiveKitSnapshot {
  roomState: ConnectionState
  isReconnecting: boolean
  error: string | null
  activeVoiceChannelId: string | null
  isMicMuted: boolean
  isDeafened: boolean
  isCameraEnabled: boolean
  isScreenShareEnabled: boolean
  isNoiseSuppressionEnabled: boolean
  isNoiseSuppressionPending: boolean
  isEchoCancellationEnabled: boolean
  micVolume: number
  speakerVolume: number
  videoQuality: VideoQuality
  screenShareQuality: ScreenShareQuality
  selectedMicId: string
  selectedCameraId: string
  selectedSpeakerId: string
  participants: ParticipantState[]
}

export interface DebugSnapshot {
  ts: number
  currentUser: User | null
  groups: Group[]
  selectedGroupId: string | null
  selectedChannelId: string | null
  groupDetails: Record<string, GroupDetails>
  groupUsers: Record<string, GroupUser[]>
  liveKit: DebugLiveKitSnapshot
  isMobile: boolean
}
